import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Dossier primitives, ported from PollSphere's `intel/shared.tsx` — plus the two this
 * platform needs and PollSphere does not have.
 *
 * `Panel`, `MetricCard`, `KpiStrip`, `ErpTable`, `Th`, `SortTh`, `Td`, `EmptyRow`,
 * `SectionHeader` and `StatusBadge` are deliberately NOT re-ported: they already exist in
 * `@/components/erp/primitives` with the same names and props.
 */

// ── Formatters ──────────────────────────────────────────────────────────────

export function fmtNum(value: number | null | undefined, dp = 0): string {
  if (value == null || Number.isNaN(value)) return '—'
  return value.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })
}

export function fmtKES(value: number | null | undefined): string {
  if (value == null) return '—'
  if (Math.abs(value) >= 1_000_000_000) return `KES ${(value / 1_000_000_000).toFixed(1)}B`
  if (Math.abs(value) >= 1_000_000) return `KES ${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1_000) return `KES ${(value / 1_000).toFixed(0)}K`
  return `KES ${fmtNum(value)}`
}

export function fmtDate(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

export function fmtRelative(value: string | null | undefined): string {
  if (!value) return '—'
  const then = new Date(value).getTime()
  if (Number.isNaN(then)) return '—'
  const days = Math.floor((Date.now() - then) / 86_400_000)
  if (days < 0) return 'in the future'
  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days}d ago`
  if (days < 365) return `${Math.floor(days / 30)}mo ago`
  return `${Math.floor(days / 365)}y ago`
}

// ── The two that matter most ────────────────────────────────────────────────

const CREDIBILITY_MEANING: Record<string, string> = {
  '1': 'confirmed by other sources',
  '2': 'probably true',
  '3': 'possibly true',
  '4': 'doubtful',
  '5': 'improbable',
  '6': 'cannot be judged',
}

const RELIABILITY_MEANING: Record<string, string> = {
  A: 'completely reliable',
  B: 'usually reliable',
  C: 'fairly reliable',
  D: 'not usually reliable',
  E: 'unreliable',
  F: 'cannot be judged',
}

/**
 * The Admiralty pair, as a two-character chip.
 *
 * **This is the single most important piece of UI in the feature.** Without it a `D5` tweet
 * and an `A1` court judgment render as the same kind of thing, and a reader briefs from both
 * in the same voice. The colour ramps on CREDIBILITY because that is the property of the
 * claim; the reliability letter is the property of the publication and both are spelled out
 * in the tooltip, because "B2" means nothing to somebody seeing it for the first time.
 */
export function GradeChip({ grade, className }: { grade: string | null | undefined
  className?: string }) {
  const value = (grade ?? '').trim().toUpperCase()
  const reliability = value.charAt(0)
  const credibility = value.charAt(1)
  if (!reliability || !credibility) {
    return <span className="font-mono text-[10px] text-muted-foreground">—</span>
  }
  const tone =
    credibility <= '2' ? 'bg-emerald-500/12 text-emerald-700 dark:text-emerald-400 border-emerald-500/30'
    : credibility === '3' ? 'bg-amber-500/12 text-amber-700 dark:text-amber-400 border-amber-500/30'
    : 'bg-rose-500/12 text-rose-700 dark:text-rose-400 border-rose-500/30'

  return (
    <span
      className={cn('inline-flex items-center rounded border px-1 font-mono text-[10px]',
        'leading-4 tabular-nums', tone, className)}
      title={`${reliability} — ${RELIABILITY_MEANING[reliability] ?? 'unknown source rating'} · `
        + `${credibility} — ${CREDIBILITY_MEANING[credibility] ?? 'unknown credibility'}`}
    >
      {value}
    </span>
  )
}

/**
 * A SOURCE's reliability letter, alone.
 *
 * Deliberately not a {@link GradeChip}: that renders an Admiralty PAIR, and a source has no
 * credibility digit — credibility is a property of a claim, not of a publication. Passing a
 * filler digit would print "F3" for a source graded F, which is a number nobody chose and a
 * reader would take at face value.
 */
export function ReliabilityChip({ reliability, note }: {
  reliability: string | null | undefined; note?: string | null
}) {
  const value = (reliability ?? '').trim().toUpperCase().charAt(0)
  if (!value) return <span className="font-mono text-[10px] text-muted-foreground">—</span>
  const tone =
    value <= 'B' ? 'bg-emerald-500/12 text-emerald-700 dark:text-emerald-400 border-emerald-500/30'
    : value === 'C' ? 'bg-amber-500/12 text-amber-700 dark:text-amber-400 border-amber-500/30'
    : 'bg-rose-500/12 text-rose-700 dark:text-rose-400 border-rose-500/30'
  return (
    <span className={cn('inline-flex items-center rounded border px-1 font-mono text-[10px]',
      'leading-4', tone)}
      title={`${value} — ${RELIABILITY_MEANING[value] ?? 'unknown source rating'}`
        + (note ? ` · ${note}` : '')}>
      {value}
    </span>
  )
}

/**
 * When this platform took custody, with a staleness tone.
 *
 * Not the same date as when the thing happened, and shown everywhere an item is shown. The
 * tone is the point: a fact collected fourteen months ago may still be true, and it is
 * certainly no longer news — a reader who cannot see that treats a stale file as a current
 * one.
 */
export function CollectedAt({ at, prefix, className }: { at: string | null | undefined
  prefix?: string; className?: string }) {
  if (!at) return <span className="text-[11px] text-muted-foreground">—</span>
  const days = Math.floor((Date.now() - new Date(at).getTime()) / 86_400_000)
  const tone = days > 365 ? 'text-muted-foreground line-through'
    : days > 90 ? 'text-muted-foreground'
    : 'text-foreground/80'
  return (
    <span className={cn('text-[11px] tabular-nums', tone, className)}
          title={`Taken into custody ${new Date(at).toLocaleString()}`}>
      {prefix ? `${prefix} ` : ''}{fmtRelative(at)}
    </span>
  )
}

// ── Badges ──────────────────────────────────────────────────────────────────

const SIDE_TONE: Record<string, string> = {
  OPPOSITION: 'bg-rose-500/12 text-rose-700 dark:text-rose-400 border-rose-500/30',
  SPOILER: 'bg-amber-500/12 text-amber-700 dark:text-amber-400 border-amber-500/30',
  INDEPENDENT: 'bg-slate-500/12 text-slate-700 dark:text-slate-300 border-slate-500/30',
  ALLY: 'bg-sky-500/12 text-sky-700 dark:text-sky-400 border-sky-500/30',
  OURS: 'bg-emerald-500/12 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
}

export function SideBadge({ side }: { side: string | null | undefined }) {
  if (!side) return null
  return <Pill className={SIDE_TONE[side] ?? ''}>{label(side)}</Pill>
}

const SEVERITY_TONE: Record<string, string> = {
  LOW: 'bg-slate-500/12 text-slate-700 dark:text-slate-300 border-slate-500/30',
  MEDIUM: 'bg-amber-500/12 text-amber-700 dark:text-amber-400 border-amber-500/30',
  HIGH: 'bg-orange-500/12 text-orange-700 dark:text-orange-400 border-orange-500/30',
  EXTREME: 'bg-rose-500/12 text-rose-700 dark:text-rose-400 border-rose-500/30',
  SEVERE: 'bg-rose-500/12 text-rose-700 dark:text-rose-400 border-rose-500/30',
}

export function SeverityBadge({ severity }: { severity: string | null | undefined }) {
  if (!severity) return null
  return <Pill className={SEVERITY_TONE[severity] ?? ''}>{label(severity)}</Pill>
}

/**
 * A close-held target is normally ABSENT rather than badged — the server omits it. This
 * badge exists for the caller who DOES hold the clearance, so they can see which rows are
 * invisible to their colleagues before quoting one in a wider meeting.
 */
export function ClearanceBadge({ clearance }: { clearance: string | null | undefined }) {
  if (!clearance || clearance === 'OPEN') return null
  const tone = clearance === 'COMPARTMENTED'
    ? 'bg-purple-500/12 text-purple-700 dark:text-purple-400 border-purple-500/30'
    : 'bg-indigo-500/12 text-indigo-700 dark:text-indigo-400 border-indigo-500/30'
  return <Pill className={tone}>{label(clearance)}</Pill>
}

const MOMENTUM: Record<string, { glyph: string; tone: string }> = {
  RISING: { glyph: '▲', tone: 'text-rose-600 dark:text-rose-400' },
  STABLE: { glyph: '＝', tone: 'text-muted-foreground' },
  DECLINING: { glyph: '▼', tone: 'text-emerald-600 dark:text-emerald-400' },
  COLLAPSING: { glyph: '▼▼', tone: 'text-emerald-700 dark:text-emerald-300' },
}

/**
 * Momentum, with the sub-MoE case rendered as flat and unexciting on purpose.
 *
 * `STABLE` means "the move was smaller than the margin of error". Giving it a neutral glyph
 * and muted tone is the visual half of that rule — a dashboard that made a 2-point move on a
 * ±4 poll look like a trend would be misinforming the campaign whatever the enum said.
 */
export function MomentumBadge({ momentum }: { momentum: string | null | undefined }) {
  if (!momentum) return <span className="text-[11px] text-muted-foreground">—</span>
  const m = MOMENTUM[momentum] ?? { glyph: '＝', tone: 'text-muted-foreground' }
  return (
    <span className={cn('inline-flex items-center gap-1 text-[11px]', m.tone)}
          title={momentum === 'STABLE'
            ? 'No move larger than the margin of error'
            : label(momentum)}>
      <span aria-hidden>{m.glyph}</span>{label(momentum)}
    </span>
  )
}

export function Pill({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex items-center rounded border px-1.5 py-0 text-[10px]',
      'font-medium uppercase leading-4 tracking-wide', className)}>
      {children}
    </span>
  )
}

// ── Layout atoms ────────────────────────────────────────────────────────────

/**
 * A score as a ring. `value` null renders an explicit "not scored" rather than a zero ring —
 * a candidate nobody has polled must never look like one polling at nothing.
 */
export function ScoreRing({ value, label: text, tone = 'primary', size = 56 }: {
  value: number | null | undefined; label: string; tone?: 'primary' | 'danger' | 'warn'
  size?: number
}) {
  const radius = (size - 8) / 2
  const circumference = 2 * Math.PI * radius
  const pct = value == null ? 0 : Math.max(0, Math.min(100, value)) / 100
  const stroke = tone === 'danger' ? 'stroke-rose-500'
    : tone === 'warn' ? 'stroke-amber-500' : 'stroke-primary'

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
           aria-label={`${text}: ${value == null ? 'not scored' : Math.round(value)}`}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" strokeWidth={4}
                className="stroke-border" />
        {value != null && (
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" strokeWidth={4}
                  className={stroke} strokeLinecap="round"
                  strokeDasharray={`${circumference * pct} ${circumference}`}
                  transform={`rotate(-90 ${size / 2} ${size / 2})`} />
        )}
        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central"
              className="fill-foreground text-[11px] font-semibold tabular-nums">
          {value == null ? '—' : Math.round(value)}
        </text>
      </svg>
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{text}</span>
    </div>
  )
}

export function LabeledBar({ label: text, value, max = 100, tone }: {
  label: string; value: number | null | undefined; max?: number; tone?: string
}) {
  const pct = value == null ? 0 : Math.max(0, Math.min(1, value / max))
  return (
    <div className="space-y-0.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] text-muted-foreground">{text}</span>
        <span className="text-[11px] font-medium tabular-nums">
          {value == null ? '—' : fmtNum(value)}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-muted">
        <div className={cn('h-1.5 rounded-full', tone ?? 'bg-primary')}
             style={{ width: `${pct * 100}%` }} />
      </div>
    </div>
  )
}

export function InfoRow({ label: text, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/40 py-1">
      <span className="shrink-0 text-[11px] text-muted-foreground">{text}</span>
      <span className="text-right text-[12px]">{children}</span>
    </div>
  )
}

export function SubHeader({ children }: { children: ReactNode }) {
  return (
    <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </h4>
  )
}

export function InitialsAvatar({ name, size = 32 }: { name: string; size?: number }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase()).join('')
  return (
    <span className="inline-flex shrink-0 items-center justify-center rounded-full bg-muted
                     font-semibold text-muted-foreground"
          style={{ width: size, height: size, fontSize: size * 0.35 }}
          aria-hidden>
      {initials || '?'}
    </span>
  )
}

export function Sparkline({ values, width = 64, height = 18 }: {
  values: number[]; width?: number; height?: number
}) {
  if (values.length < 2) {
    return <span className="text-[10px] text-muted-foreground">—</span>
  }
  const max = Math.max(...values, 1)
  const min = Math.min(...values, 0)
  const span = max - min || 1
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width
    const y = height - ((v - min) / span) * height
    return `${x.toFixed(1)},${y.toFixed(1)}`
  }).join(' ')
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img"
         aria-label={`Trend: ${values.join(', ')}`}>
      <polyline points={points} fill="none" strokeWidth={1.5}
                className="stroke-primary" strokeLinejoin="round" />
    </svg>
  )
}

export function HeatCell({ value, max = 100 }: { value: number | null; max?: number }) {
  if (value == null) return <span className="text-[11px] text-muted-foreground">—</span>
  const intensity = Math.max(0, Math.min(1, value / max))
  return (
    <span className="inline-flex min-w-8 justify-center rounded px-1 text-[11px] tabular-nums"
          style={{ backgroundColor: `color-mix(in oklab, var(--color-primary) ${
            Math.round(intensity * 70)}%, transparent)` }}>
      {Math.round(value)}
    </span>
  )
}

export function TabBar({ tabs, active, onSelect }: {
  tabs: { code: string; label: string; count?: number; available?: boolean; hint?: string }[]
  active: string | null
  onSelect: (code: string) => void
}) {
  return (
    <div className="axis-table-scroll -mb-px flex gap-0.5 border-b border-border" role="tablist">
      {tabs.map((tab) => {
        const disabled = tab.available === false
        return (
          <button
            key={tab.code}
            role="tab"
            aria-selected={active === tab.code}
            disabled={disabled}
            onClick={() => !disabled && onSelect(tab.code)}
            // A tab the caller may not read is DISABLED, never hidden: a vanished tab reads
            // as "there is nothing here", and the analyst should be able to see that
            // something exists they are not cleared for.
            title={disabled ? 'Your authorities do not include this category' : tab.hint}
            className={cn(
              'shrink-0 whitespace-nowrap border-b-2 px-2.5 py-1.5 text-[12px] transition-colors',
              disabled && 'cursor-not-allowed opacity-40',
              active === tab.code
                ? 'border-primary font-medium text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground')}
          >
            {tab.label}
            {tab.count != null && tab.count > 0 && (
              <span className="ml-1 rounded bg-muted px-1 text-[10px] tabular-nums">
                {tab.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

export function label(code: string | null | undefined): string {
  if (!code) return '—'
  const spaced = code.replace(/_/g, ' ').toLowerCase()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}
